import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { effectivePrice } from "../lib/price";
import { useAuth } from "./AuthContext";
import { bcEmit, bcSubscribe, CHAN_CART } from "../lib/broadcast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext();

const STORAGE_KEY = "jns_cart";
// iter237 — survives OAuth page-reload so we can detect a "guest had items
// + just signed in" scenario even when the previous anonymous tree was
// replaced by a fresh React mount (Google OAuth redirect flow).
const ANON_FLAG_KEY = "jns_cart_anonymous";

const readLocal = () => {
    try {
        const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
};

const readAnonFlag = () => {
    try {
        return typeof window !== "undefined" && localStorage.getItem(ANON_FLAG_KEY) === "1";
    } catch { return false; }
};

const setAnonFlag = (v) => {
    try {
        if (typeof window === "undefined") return;
        if (v) localStorage.setItem(ANON_FLAG_KEY, "1");
        else localStorage.removeItem(ANON_FLAG_KEY);
    } catch { /* noop */ }
};

// Merge two cart arrays — same (item_id, size) → max(quantity)
const mergeCarts = (a, b) => {
    const map = new Map();
    const k = (p) => `${p.item_id}::${p.size || ""}`;
    for (const p of [...(a || []), ...(b || [])]) {
        const key = k(p);
        const existing = map.get(key);
        if (!existing) {
            map.set(key, { ...p });
        } else {
            const max = Math.max(existing.quantity || 0, p.quantity || 0);
            const avail = Math.min(existing.available || Infinity, p.available || Infinity);
            map.set(key, { ...existing, ...p, quantity: Math.min(max, avail || max), available: avail === Infinity ? existing.available : avail });
        }
    }
    return Array.from(map.values());
};

export const CartProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [items, setItems] = useState(() => readLocal());
    // Session-only memory of items removed from cart so the user can add them back.
    // Cleared on full reload / new tab — by design (no persistence across sessions).
    const [recentlyRemoved, setRecentlyRemoved] = useState([]);
    const hydratedRef = useRef(false);
    const syncTimer = useRef(null);
    const lastUserIdRef = useRef(null);
    // Tracks whether THIS browser tab observed the user being anonymous at
    // some point during its lifetime. Only flipped true when AuthContext
    // settles with `user=null`. Used by the cart-hydration effect to
    // distinguish a true "guest → signed-in" transition (where merging the
    // local guest cart is correct) from a "signed-in user opens a fresh
    // tab/device" event (where the server cart is the source of truth and
    // the stale localStorage from a previous device session must NOT be
    // merged — that's what was resurrecting deleted items across devices).
    const wasAnonInThisTabRef = useRef(false);

    // Persist locally on every change + broadcast to other tabs.
    // While the user is anonymous, also flip a localStorage flag so a
    // subsequent OAuth round-trip (page reload) can still detect that
    // this browser had a guest cart waiting to be merged.
    const isRemoteUpdateRef = useRef(false);
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* noop */ }
        if (!user && items.length > 0) {
            setAnonFlag(true);
        }
        if (isRemoteUpdateRef.current) {
            // Don't echo back broadcasts we just received from another tab
            isRemoteUpdateRef.current = false;
            return;
        }
        bcEmit(CHAN_CART, items);
    }, [items, user]);

    // Subscribe to cart updates from other tabs in the same browser
    useEffect(() => {
        return bcSubscribe(CHAN_CART, (remoteItems) => {
            if (!Array.isArray(remoteItems)) return;
            isRemoteUpdateRef.current = true;
            setItems(remoteItems);
        });
    }, []);

    // On login/logout: pull server cart as the SOURCE OF TRUTH. We only
    // merge with the local guest cart when this tab observed the user
    // transition from anonymous → signed-in just now (i.e. the customer
    // signed in inside this tab). On any other case — fresh tab/device
    // load with an already-valid session, or a different user — the
    // local cart is treated as stale and replaced with the server's.
    //
    // iter237 — Google OAuth completes via a full page redirect, which
    // remounts the entire React tree. In that scenario `wasAnonInThisTab`
    // starts at `false` again and we used to drop the guest cart on the
    // floor. The persistent `ANON_FLAG_KEY` in localStorage closes that
    // gap: it's set whenever the cart is touched while signed out and
    // survives the OAuth redirect, so we still recognise the merge case.
    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            // Tab is now (or still) anonymous — record it so a subsequent
            // sign-in inside the same tab triggers the merge path.
            wasAnonInThisTabRef.current = true;
            hydratedRef.current = false;
            lastUserIdRef.current = null;
            return;
        }
        if (lastUserIdRef.current === user.user_id) return;
        lastUserIdRef.current = user.user_id;

        const local = readLocal();
        const isFreshSignIn = (wasAnonInThisTabRef.current || readAnonFlag()) && local.length > 0;
        axios.get(`${API}/cart`, { withCredentials: true })
            .then((r) => {
                const server = r.data?.items || [];
                // Fresh sign-in in THIS tab AND there was a local guest cart →
                // merge. Otherwise (re-opened tab, different device, or just a
                // logged-in browser refresh) the server is canonical.
                const next = isFreshSignIn ? mergeCarts(local, server) : server;
                hydratedRef.current = true;
                wasAnonInThisTabRef.current = false; // consumed
                setAnonFlag(false); // anonymous cart is now owned by this user
                setItems(next);
                // Only re-push the merged result if a real merge happened —
                // a pure replace doesn't need a write.
                if (isFreshSignIn) {
                    axios.put(`${API}/cart`, { items: next }, { withCredentials: true }).catch(() => {});
                }
            })
            .catch(() => { hydratedRef.current = true; });
    }, [user, authLoading]);

    // Debounced sync to server whenever cart changes (only for logged-in users post-hydration)
    useEffect(() => {
        if (!user || !hydratedRef.current) return;
        if (syncTimer.current) clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
            axios.put(`${API}/cart`, { items }, { withCredentials: true }).catch(() => {});
        }, 600);
        return () => { if (syncTimer.current) clearTimeout(syncTimer.current); };
    }, [items, user]);

    // Push current cart to the server immediately. Used by destructive ops
    // (remove/update-down/clear) so a quick tab close right after deleting
    // doesn't lose the deletion to the 600 ms debounce window. Also bypasses
    // the React `items` state lag by accepting the snapshot to push.
    const flushNow = useRef((snap) => {
        if (!lastUserIdRef.current) return;
        if (syncTimer.current) { clearTimeout(syncTimer.current); syncTimer.current = null; }
        axios.put(`${API}/cart`, { items: snap }, { withCredentials: true }).catch(() => {});
    }).current;

    // Final safety net: if there's a pending sync when the page is hidden
    // or unloaded (tab close, navigate away, mobile background), fire a
    // `sendBeacon` so the server always sees the latest state. Beacons
    // are queued by the OS even when the document is gone, unlike `fetch`.
    //
    // Crucially: when the tab becomes VISIBLE again, we re-fetch the
    // server cart so a tab that's been backgrounded for hours can't
    // beacon-overwrite the canonical server state with its now-stale
    // in-memory snapshot. This was Bug #2 in the cross-device resurrection
    // (mobile deletes → desktop tab focuses again → beacon-flushed stale
    // items → ghosts return on the next mobile load).
    useEffect(() => {
        if (!user) return;
        const flushBeacon = () => {
            // Don't fire until hydration completes — otherwise the very
            // first hidden event after sign-in could blast pre-merge
            // local-only items at the server.
            if (!hydratedRef.current) return;
            if (!navigator.sendBeacon) return;
            try {
                const blob = new Blob([JSON.stringify({ items })], { type: "application/json" });
                navigator.sendBeacon(`${API}/cart/beacon`, blob);
            } catch { /* ignore */ }
        };
        const onVisibility = () => {
            if (document.visibilityState === "hidden") {
                flushBeacon();
            } else if (document.visibilityState === "visible" && lastUserIdRef.current) {
                // Refresh from server — another device may have touched
                // the cart while this tab was backgrounded.
                axios.get(`${API}/cart`, { withCredentials: true })
                    .then((r) => {
                        const server = r.data?.items || [];
                        // Only replace if it's *actually* different to avoid
                        // a pointless render and broadcast cycle.
                        const cur = JSON.stringify(items.map(p => [p.item_id, p.size, p.quantity]));
                        const nxt = JSON.stringify(server.map(p => [p.item_id, p.size, p.quantity]));
                        if (cur !== nxt) setItems(server);
                    })
                    .catch(() => { /* ignore — beacon will reconcile next */ });
            }
        };
        window.addEventListener("pagehide", flushBeacon);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("pagehide", flushBeacon);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [items, user]);

    const add = (item, qty = 1, opts = {}) => {
        // B2 analytics — fire add_to_cart beacon (best-effort, never blocks UX)
        try {
            import("../lib/analyticsBeacon").then(({ trackAddToCart }) => {
                trackAddToCart(item?.id, { qty, size: opts?.size || null });
            }).catch(() => {});
        } catch { /* noop */ }
        const size = opts.size || null;
        const availForSize = opts.availableForSize != null ? opts.availableForSize : null;
        const price = effectivePrice(item);
        const available = availForSize !== null ? availForSize : item.quantity;
        // Enrichment fields (drive delivery/MRP/exchange chips + dept coupons).
        // Computed once so BOTH the new-line and existing-line branches carry
        // them — otherwise a line hydrated slim from the server (or added
        // before these fields existed) would never gain them on re-add.
        const enrichment = {
            department_slug: item.department_slug || null,
            category: item.category || null,
            mrp: item.selling_price && price < item.selling_price ? item.selling_price : null,
            min_delivery_days: item.min_delivery_days || null,
            max_delivery_days: item.max_delivery_days || null,
            is_returnable: (item.is_returnable === true || item.is_returnable === false)
                ? item.is_returnable
                : null,
            low_stock_threshold: item.low_stock_threshold || null,
        };
        setItems(prev => {
            const existing = prev.find(p => p.item_id === item.id && (p.size || null) === size);
            if (existing) {
                return prev.map(p => p === existing ? { ...p, ...enrichment, unit_price: price, available, quantity: Math.min(p.quantity + qty, available) } : p);
            }
            return [...prev, {
                item_id: item.id,
                item_name: item.name,
                photo_url: item.photo_url || (item.photo_urls && item.photo_urls[0]) || null,
                unit_price: price,
                quantity: Math.min(qty, available),
                available,
                size,
                ...enrichment,
            }];
        });
        // Cross-sell trigger — broadcast every add so a global listener can
        // decide whether to surface the "Complete the look" cross-sell modal.
        // Best-effort + fire-and-forget: it never mutates cart state and is
        // wrapped so a failure here can never break an add-to-cart.
        try {
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("jns:item-added", { detail: { item } }));
            }
        } catch { /* noop */ }
    };

    const update = (item_id, quantity, size = null) => {
        setItems(prev => {
            const next = prev.map(p => (p.item_id === item_id && (p.size || null) === size) ? { ...p, quantity: Math.max(1, Math.min(quantity, p.available)) } : p);
            // Quantity changes are user-intent — flush immediately so a
            // quick tab close doesn't lose the new count.
            if (hydratedRef.current) flushNow(next);
            return next;
        });
    };

    const remove = (item_id, size = null) => {
        setItems(prev => {
            const victim = prev.find(p => p.item_id === item_id && (p.size || null) === size);
            if (victim) {
                setRecentlyRemoved(rr => {
                    const filtered = rr.filter(r => !(r.item_id === victim.item_id && (r.size || null) === (victim.size || null)));
                    return [{ ...victim, removed_at: Date.now() }, ...filtered].slice(0, 8);
                });
            }
            const next = prev.filter(p => !(p.item_id === item_id && (p.size || null) === size));
            // Immediate sync — deletion must persist even if the user
            // closes the tab in the next 600 ms (root cause of the
            // "items reappear after I cleared my cart" bug).
            if (hydratedRef.current) flushNow(next);
            return next;
        });
    };

    const restoreRemoved = (item_id, size = null) => {
        const r = recentlyRemoved.find(it => it.item_id === item_id && (it.size || null) === size);
        if (!r) return;
        setRecentlyRemoved(prev => prev.filter(it => !(it.item_id === item_id && (it.size || null) === size)));
        setItems(prev => {
            const exists = prev.some(p => p.item_id === item_id && (p.size || null) === size);
            if (exists) return prev;
            // Drop the removed_at marker before re-adding
            const { removed_at: _ra, ...rest } = r;
            return [...prev, rest];
        });
    };

    const dismissRemoved = (item_id, size = null) => {
        setRecentlyRemoved(prev => prev.filter(it => !(it.item_id === item_id && (it.size || null) === size)));
    };

    const clear = () => {
        setItems([]);
        if (user) {
            // Cancel any debounce so the deletion definitely lands on the
            // server, and use DELETE which is the dedicated clear endpoint.
            if (syncTimer.current) { clearTimeout(syncTimer.current); syncTimer.current = null; }
            axios.delete(`${API}/cart`, { withCredentials: true }).catch(() => {});
        }
    };

    const total = items.reduce((a, p) => a + p.unit_price * p.quantity, 0);
    const count = items.reduce((a, p) => a + p.quantity, 0);

    return (
        <CartContext.Provider value={{ items, add, update, remove, clear, total, count, recentlyRemoved, restoreRemoved, dismissRemoved }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
